import { ref, computed, toValue } from 'vue'

import { ImageSrcSetFormat } from '@xcel-next/rendering-tools/types/constants'
import { ProductType } from '@xcel-next/core/enums'

import { useConfigStore } from '../stores/config'
import { useImgSrcSet } from './use-img-src-set'
import { hashString } from '../utils/strings'

import type { MaybeRefOrGetter } from 'vue'
import type { ComposedProduct, ComposedProductAmazon, ComposedProductAwin, ComposedProductManual, ProsAndCons, TechnicalDataItem } from '@xcel-next/composer-schema'
import type { ImageObject } from './use-img-src-set'

export type OtherPartner = {
  partner?: string,
  url?: string
}

type ProductCommonFields = {
  label?: string,
  shoppingDaysLabel?: string,
  title?: string,
  showPrice?: boolean,
  otherPartners?: OtherPartner[],
  prosAndCons?: ProsAndCons,
  technicalData?: { show?: boolean, items?: TechnicalDataItem[] },
  editorialStarRating?: number,
  pricePerUnit?: { price?: number, unit?: string }
}

export type RawProduct =
  | { productType: ProductType.amazon, [ProductType.amazon]: ComposedProductAmazon }
  | { productType: ProductType.manual, [ProductType.manual]: ComposedProductManual }
  | { productType: ProductType.awin, [ProductType.awin]: ComposedProductAwin }


export type UnifiedProduct = {
  type: string,
  id: null,
  link: { url: string, target: string | undefined, rel: string },
  label?: string,
  shoppingDaysLabel?: string,
  image?: string | null,
  roofline?: string,
  title?: string,
  showPrime: boolean,
  showPrice: boolean,
  sellingPrice?: number,
  listPrice?: number,
  lastKnownPrice?: number | null,
  saving?: number,
  outOfStock: boolean,
  button: string,
  otherPartners?: OtherPartner[],
  prosAndCons?: ProsAndCons,
  technicalData?: TechnicalDataItem[],
  editorialStarRating?: number,
  extendedInformationOrder: string[],
  pricePerUnit?: string,
  uniqueId: string
}

type UseProductGroupOptions = {
  brand?: MaybeRefOrGetter<string | undefined>,
  image?: MaybeRefOrGetter<ImageObject | undefined>,
  products?: MaybeRefOrGetter<(ComposedProduct | undefined)[] | undefined>,
  productGroupType?: MaybeRefOrGetter<string | undefined>,
  affiliateType?: MaybeRefOrGetter<string | undefined>,
  isPreview?: MaybeRefOrGetter<boolean | undefined>
}

export function useProductGroup (options: UseProductGroupOptions = {}) {
  const configStore = useConfigStore()
  const { createImgSrc } = useImgSrcSet()

  const outOfStockProductExists = ref(false)

  function calcSaving (listPrice: number, sellingPrice: number): number {
    return Math.round(100 / listPrice * (listPrice - sellingPrice))
  }

  function formatCurrencyString (cent: number): string {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cent / 100)
  }

  function pricePerUnitString (pricePerUnit?: { price?: number, unit?: string }): string | undefined {
    if (!pricePerUnit?.price || !pricePerUnit?.unit) return undefined
    return `${formatCurrencyString(pricePerUnit.price)} / ${pricePerUnit.unit}`
  }

  function getProductTechnicalData (product: ProductCommonFields): TechnicalDataItem[] | undefined {
    return product.technicalData?.show && product.technicalData?.items?.length ? product.technicalData.items : undefined
  }

  // check available extended information for single
  // Attention: Order equal to render position is important!
  function getExtendedInformationOrder (product: ProductCommonFields): string[] {
    const extendedInformation = {
      otherPartners: !!product.otherPartners,
      editorialStarRating: !!product.editorialStarRating,
      prosAndCons: !!product.prosAndCons,
      technicalData: !!(product.technicalData?.show && product.technicalData?.items?.length)
    }
    // just keep active elements in the order to identify position inside of the component
    return Object.keys(extendedInformation).filter(key => extendedInformation[key as keyof typeof extendedInformation])
  }

  function unifyAmazon (data: ComposedProductAmazon, type: string): UnifiedProduct {
    if (!data.listPrice) {
      outOfStockProductExists.value = true
    }

    return {
      type,
      id: null,
      link: {
        url: data.affiliateUrl ?? '',
        target: configStore.rsConfig.productLinkTarget,
        rel: 'sponsored noopener'
      },
      label: data.label,
      shoppingDaysLabel: data?.shoppingDaysLabel,
      image: data.altImage && data.altImage.imageId
        ? createImgSrc({
          image: data.altImage,
          brand: toValue(options.brand) ?? '',
          setName: 'productGroupImage',
          srcSetDefinition: {
            format: ImageSrcSetFormat.Landscape
          }
        })
        : data.image && data.image.url ? data.image.url : undefined,
      roofline: 'Amazon',
      title: data.title,
      showPrime: !!data.isEligibleForPrime || !!data.isPrimeExclusive,
      showPrice: !!data.showPrice,
      sellingPrice: (data.lowestNewPrice && data.listPrice) ? Math.min(data.lowestNewPrice, data.listPrice) : data.listPrice ? data.listPrice : undefined,
      listPrice: data.saving !== 0 ? data.listPrice : undefined,
      lastKnownPrice: data.lastKnownPrice || null,
      saving: data.saving !== 0 ? calcSaving(data.listPrice ?? 0, data.lowestNewPrice ?? 0) : undefined,
      outOfStock: !data.listPrice,
      button: !data.listPrice ? 'Verfügbarkeit prüfen' : 'Zum Produkt',
      otherPartners: data.otherPartners || undefined,
      prosAndCons: data.prosAndCons || undefined,
      technicalData: getProductTechnicalData(data),
      editorialStarRating: data.editorialStarRating || undefined,
      extendedInformationOrder: getExtendedInformationOrder(data),
      pricePerUnit: pricePerUnitString(data.pricePerUnit),
      uniqueId: hashString(data.affiliateUrl ?? '')
    }
  }

  function unifyManual (data: ComposedProductManual, type: string): UnifiedProduct {
    return {
      type,
      id: null,
      link: {
        url: data.url ?? '',
        target: configStore.rsConfig.productLinkTarget,
        rel: `${data.rel} noopener`
      },
      label: data.label,
      shoppingDaysLabel: data.shoppingDaysLabel,
      image: data.image
        ? createImgSrc({
          image: data.image,
          brand: toValue(options.brand) ?? '',
          setName: 'productGroupImage',
          srcSetDefinition: {
            format: ImageSrcSetFormat.Landscape
          }
        })
        : undefined,
      roofline: data.roofLine,
      title: data.title,
      showPrime: false,
      showPrice: !!data.showPrice,
      sellingPrice: data.price,
      listPrice: (data.strikePrice ?? 0) > 0 ? data.strikePrice : undefined,
      saving: (data.strikePrice ?? 0) > 0 ? calcSaving(data.strikePrice ?? 0, data.price ?? 0) : undefined,
      outOfStock: false,
      button: 'Zum Produkt',
      otherPartners: data.otherPartners || undefined,
      prosAndCons: data.prosAndCons || undefined,
      technicalData: getProductTechnicalData(data),
      editorialStarRating: data.editorialStarRating || undefined,
      extendedInformationOrder: getExtendedInformationOrder(data),
      pricePerUnit: pricePerUnitString(data.pricePerUnit),
      uniqueId: hashString(data.url ?? '')
    }
  }

  function unifyAwin (data: ComposedProductAwin, type: string): UnifiedProduct {
    return {
      type,
      id: null,
      link: {
        url: data.url ?? '',
        target: configStore.rsConfig.productLinkTarget,
        // awin has no editor-controlled `rel` field (unlike manual) - fixed value, same convention as amazon
        rel: 'sponsored noopener'
      },
      label: data.label,
      shoppingDaysLabel: data.shoppingDaysLabel,
      image: data.image?.imageId
        ? createImgSrc({
          image: data.image,
          brand: toValue(options.brand) ?? '',
          setName: 'productGroupImage',
          srcSetDefinition: {
            format: ImageSrcSetFormat.Landscape
          }
        })
        : data.imageUrl ? data.imageUrl : undefined,
      roofline: data.merchantName,
      title: data.title,
      showPrime: false,
      showPrice: !!data.showPrice,
      sellingPrice: data.price,
      listPrice: (data.oldprice ?? 0) > 0 ? data.oldprice : undefined,
      saving: (data.oldprice ?? 0) > 0 ? calcSaving(data.oldprice ?? 0, data.price ?? 0) : undefined,
      outOfStock: false,
      button: !data.price ? 'Verfügbarkeit prüfen' : 'Zum Produkt',
      otherPartners: data.otherPartners || undefined,
      prosAndCons: data.prosAndCons || undefined,
      technicalData: getProductTechnicalData(data),
      editorialStarRating: data.editorialStarRating || undefined,
      extendedInformationOrder: getExtendedInformationOrder(data),
      pricePerUnit: pricePerUnitString({
        price: data.baseprice,
        // ensure that amounts unequal 1 will be displayed to ensure correct baseprice info
        unit: (data.basepriceamount !== 1 ? data.basepriceamount + ' ' : '') + data.basepricetext
      }),
      uniqueId: hashString(data.url ?? '')
    }
  }

  function unifyProducts (products: (ComposedProduct | undefined)[] | null | undefined = []): UnifiedProduct[] {
    return (products ?? []).flatMap(product => {
      if (!product) return []
      switch (product.productType) {
        case ProductType.amazon: return product.amazon ? [unifyAmazon(product.amazon, product.productType)] : []
        case ProductType.manual: return product.manual ? [unifyManual(product.manual, product.productType)] : []
        case ProductType.awin: return product.awin ? [unifyAwin(product.awin, product.productType)] : []
        default: return []
      }
    })
  }

  const unifiedProducts = computed(() => unifyProducts(toValue(options.products)))

  // check if at least one product has the given property
  function propertyExists (propertyName: keyof UnifiedProduct): boolean {
    return unifiedProducts.value.some(product =>
      product[propertyName] !== null && product[propertyName] !== undefined && product[propertyName] !== ''
    )
  }

  function hasManualProduct (): boolean {
    return toValue(options.products)?.some(product => product?.productType === ProductType.manual) ?? false
  }

  const labelImageUrl = computed(() => {
    const image = toValue(options.image)
    return image?.mimetype
      ? createImgSrc({
        image,
        brand: toValue(options.brand) ?? '',
        setName: 'productGroupLabel',
        type: 'webp',
        srcSetDefinition: {
          format: ImageSrcSetFormat.Landscape
        }
      })
      : undefined
  })

  const productGroupStyleClasses = {
    ['bx-content']: true,
    ['bx-content--structure-full']: true,
    ['bx-product-group']: true,
    ['bx-typo--product-group']: true
  }

  const innerGroupStyleClasses = computed(() => {
    const productGroupType = toValue(options.productGroupType)
    return {
      ['bx-slider']: productGroupType === 'slider',
      ['bx-slider--product-group']: productGroupType === 'slider',
      ['bx-product-group--oos-product-exists']: outOfStockProductExists.value,
      ['bx-product-group--single']: productGroupType === 'single',
      ['bx-product-group--has-tech-data']: productGroupType === 'single' && propertyExists('technicalData'),
      ['bx-product-group__inner']: true
    }
  })

  const badgeStyleClasses = computed(() => {
    const productGroupType = toValue(options.productGroupType)
    const affiliateType = toValue(options.affiliateType)
    return {
      ['bx-badge']: true,
      ['bx-badge--affiliate']: productGroupType === 'single' || affiliateType === 'affiliate-link',
      ['bx-badge--affiliates']: productGroupType !== 'single'
    }
  })

  const productStyleClasses = computed(() => {
    const productGroupType = toValue(options.productGroupType)
    return {
      ['bx-product']: true,
      ['bx-product--single']: productGroupType === 'single',
      ['bx-product--rebrush']: productGroupType === 'single' || productGroupType === 'autocomparison',
      ['bx-slider__cell']: productGroupType === 'slider'
    }
  })

  /**
   * Synchronizes the heights of horizontal product labels to ensure
   * that product information (like title, price, etc.) aligns consistently
   * across a row, at least when labels wrap and have varying text lengths.
   *
   * This method calculates the maximum height for each type of label
   * (regular product label and "shopping days" label) and sets it
   * as a CSS variable on all corresponding elements.
   *
   * The logic avoids unnecessary DOM writes if:
   *   - all labels already have the same height
   *   - the height is zero
   *   - the height exceeds a reasonable threshold (e.g., 75px)
   *
   * Additionally, the method is skipped in preview mode.
   */
  function syncLabelHeights (rootEl: Element | null | undefined): void {
    // Skip execution in preview mode (e.g., CMS preview)
    if (toValue(options.isPreview) || !rootEl) return

    // Define the label selectors and their corresponding CSS variable names
    const productLabels = [
      { selector: '.bx-product__label:not(.bx-product__label--shopping-days)', cssVar: '--max-horizontal-product-label-height' },
      { selector: '.bx-product__label--shopping-days', cssVar: '--max-horizontal-shopping-days-label-height' }
    ]

    // Loop through each label type
    productLabels.forEach(({ selector, cssVar }) => {
      // Query all matching label elements in the current component
      const labelContainers = rootEl.querySelectorAll<HTMLElement>(selector)
      if (!labelContainers.length) return // skip if none found

      // Collect all heights of the label elements
      const heights = Array.from(labelContainers, el => el.offsetHeight)
      const maxHeight = Math.max(...heights)

      // Skip if no visible height or height exceeds a threshold
      if (maxHeight === 0 || maxHeight > 75) return

      // Check if all labels are already the same height as maxHeight
      const allEqual = heights.every(h => h === heights[0])
      if (allEqual && heights[0] === maxHeight) return // skip if no adjustment needed

      // Apply the max height to all label elements via CSS variable
      labelContainers.forEach(el => {
        el.style.setProperty(cssVar, `${maxHeight}px`)
      })
    })
  }

  return {
    outOfStockProductExists,
    unifiedProducts,
    unifyProducts,
    propertyExists,
    hasManualProduct,
    labelImageUrl,
    productGroupStyleClasses,
    innerGroupStyleClasses,
    badgeStyleClasses,
    productStyleClasses,
    formatCurrencyString,
    syncLabelHeights
  }
}
